import { useMutation, useQueryClient } from 'react-query';
import notify from '../../../../../../../shared/feedback/notifications';
import { Translate } from '../../../../../../../shared/LanguageService';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { useTelemetryContext } from '../../../../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../../../../telemetry/utils';
import ImageProcessingApi from '../../../api/ImageProcessingApi';
import { ImageType, IOrderRow } from '../../../Entities';
import { photoStatusQueryKey } from '../../entry/queries/use-query-photo-status';
import { imagesQueryKey } from '../../listImages/queries/use-query-images';

type DeleteImagesMutationArgs = {
    orderId: number;
    user: AuthenticatedUser;
};

export type DeleteImagesResult = {
    deletedIds: number[];
    failedIds: number[];
};

type DeleteImagesContext = {
    previousOrderRows: IOrderRow[] | undefined;
    previousUpdatedAt: number | undefined;
};

function withoutThumbnails(orderRows: IOrderRow[], imageIds: number[]): IOrderRow[] {
    return orderRows.map((orderRow) => ({
        ...orderRow,
        thumbnails: orderRow.thumbnails.filter((thumbnail) => !imageIds.includes(thumbnail.id))
    }));
}

export function useDeleteImagesMutation({ orderId, user }: DeleteImagesMutationArgs) {
    let queryClient = useQueryClient();
    let { logger } = useTelemetryContext();
    let editedImagesKey = [imagesQueryKey, { orderId, imageType: ImageType.EDITED }];

    return useMutation<DeleteImagesResult, unknown, number[], DeleteImagesContext>(
        async (imageIds: number[]): Promise<DeleteImagesResult> => {
            let deletedIds: number[] = [];
            let failedIds: number[] = [];

            for (let imageId of imageIds) {
                try {
                    await ImageProcessingApi.deleteEditedPhoto(orderId, imageId);
                    deletedIds.push(imageId);
                } catch (error) {
                    failedIds.push(imageId);
                    logger.logException(error, {
                        origin: 'ImageEditorImageProcessingPage.Mutation.deleteEditedPhoto',
                        userInfo: getUserInfo(user),
                        imageId
                    });
                }
            }

            return { deletedIds, failedIds };
        },
        {
            onMutate: async (imageIds) => {
                await queryClient.cancelQueries(editedImagesKey);
                let previousState = queryClient.getQueryState<IOrderRow[]>(editedImagesKey);
                let previousOrderRows = previousState ? previousState.data : undefined;
                let previousUpdatedAt = previousState ? previousState.dataUpdatedAt : undefined;

                if (previousOrderRows) {
                    queryClient.setQueryData(
                        editedImagesKey,
                        withoutThumbnails(previousOrderRows, imageIds),
                        { updatedAt: previousUpdatedAt }
                    );
                }

                return { previousOrderRows, previousUpdatedAt };
            },
            onError: (error, imageIds, context) => {
                if (context && context.previousOrderRows) {
                    queryClient.setQueryData(editedImagesKey, context.previousOrderRows, {
                        updatedAt: context.previousUpdatedAt
                    });
                }
            },
            onSuccess: ({ deletedIds, failedIds }, imageIds, context) => {
                if (failedIds.length > 0) {
                    notify.failure(
                        Translate('image-processing/button-label/delete-multiple/error')
                    );

                    if (context && context.previousOrderRows) {
                        queryClient.setQueryData(
                            editedImagesKey,
                            withoutThumbnails(context.previousOrderRows, deletedIds),
                            { updatedAt: context.previousUpdatedAt }
                        );
                    }
                }

                if (deletedIds.length === 0) {
                    return;
                }

                notify.success(Translate('image-processing/button-label/delete-multiple/success'));

                return Promise.all([
                    queryClient.invalidateQueries([imagesQueryKey, { orderId }]),
                    queryClient.invalidateQueries([photoStatusQueryKey, { orderId }])
                ]);
            }
        }
    );
}
